import {
  CLAIM_LIST_REQUEST,
  CLAIM_LIST_SUCCESS,
  CLAIM_LIST_FAILURE,
  CLAIM_DETAIL_REQUEST,
  CLAIM_DETAIL_SUCCESS,
  CLAIM_DETAIL_FAILURE,
  CLAIM_ACTION_REQUEST,
  CLAIM_ACTION_SUCCESS,
  CLAIM_ACTION_FAILURE,
  UPDATE_CLAIM_FILTERS
} from '../../actions/claimActions'

/**
 * El trozo `claim` del store: el Libro de Reclamaciones.
 *
 * ─── Por qué hay un contador de acciones ────────────────────────────────────
 *
 * El middleware de la API **resuelve su promesa también cuando la petición
 * falla**: despacha el `_FAILURE` y resuelve. Así que un
 * `try { await accion() } catch` nunca entra al `catch`, y la pantalla avisaba
 * «Respuesta oficial emitida y notificada al consumidor» aunque la API la
 * hubiera rechazado. En este módulo eso no es un aviso optimista: es decirle a
 * un operador que un consumidor fue notificado cuando no lo fue.
 *
 * Quien sabe si una acción salió es el store. `acciones` sube sólo con
 * `_SUCCESS`, y `saveError` sólo con `_FAILURE`; la pantalla mira los dos.
 *
 * ─── Por qué el detalle no se borra al recargar ─────────────────────────────
 *
 * `CLAIM_DETAIL_REQUEST` ponía `currentClaim: null`, y la pantalla hacía
 * `if (detailLoading || !reclamo) return <spinner/>`. Entre las dos cosas,
 * cada acción hacía desaparecer la hoja entera y repintarla desde cero, y un
 * fallo al abrir dejaba el spinner **girando para siempre**, porque
 * `detailError` no lo leía nadie.
 *
 * ─── `error` del listado y `detailError` de la ficha son dos cosas ──────────
 *
 * Estaban en el mismo campo, así que un fallo al cargar la lista se asomaba en
 * la ficha y al revés.
 */
export const initialState = {
  data: [],
  count: 0,
  /** Cuántas hojas trae una página; se aprende de la respuesta. */
  porPagina: 0,
  loading: false,
  error: null,
  /**
   * Todos vacíos a propósito: el libro se abre **sin filtros**. Así la API
   * aplica su orden por omisión —`-created_at`, lo último que entró arriba— y
   * el operador ve el libro entero, que es lo que espera de un libro.
   */
  filters: {
    /* Los tres se eligen de varios en varios; `paramsDeFiltros` los traduce. */
    status: [],
    claim_type: [],
    service_category: [],
    search: '',
    ordering: '',
    page: 1
  },
  /**
   * Los parámetros que produjeron los datos que hay ahora mismo. No es lo
   * mismo que `filters`: entre que se cambia un filtro y llega la respuesta,
   * `filters` ya cambió y los datos son los de antes. Con esto se puede
   * comprobar si el servidor hizo caso al filtro que se le mandó.
   */
  aplicados: {},
  currentClaim: null,
  detailLoading: false,
  detailError: null,
  isSaving: false,
  saveError: null,
  /** Sube con cada acción que la API confirma. */
  acciones: 0,
  needRefresh: false
}

/** La API pagina al estilo DRF, pero un día devolvió la lista a secas. */
const listaDe = datos => {
  if (Array.isArray(datos)) return datos
  if (datos && Array.isArray(datos.results)) return datos.results
  return []
}

const errorDe = action => action.errors || action.error || action.data || {}

export default function claimReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_CLAIM_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        needRefresh: true
      }

    case CLAIM_LIST_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        needRefresh: false
      }

    case CLAIM_LIST_SUCCESS: {
      const payload = action.data || action.response || {}
      const results = listaDe(payload)
      return {
        ...state,
        loading: false,
        error: null,
        data: results,
        count: payload.count !== undefined ? payload.count : results.length,
        porPagina: Math.max(state.porPagina, results.length),
        aplicados: action.extra || {}
      }
    }

    case CLAIM_LIST_FAILURE:
      // Sin vaciar la lista, la pantalla enseñaría las hojas de antes como si
      // la consulta nueva las hubiera devuelto.
      return {
        ...state,
        loading: false,
        data: [],
        count: 0,
        error: errorDe(action)
      }

    case CLAIM_DETAIL_REQUEST:
      return {
        ...state,
        detailLoading: true,
        detailError: null
      }

    case CLAIM_DETAIL_SUCCESS:
      return {
        ...state,
        detailLoading: false,
        detailError: null,
        currentClaim: action.data || action.response || null
      }

    case CLAIM_DETAIL_FAILURE:
      return {
        ...state,
        detailLoading: false,
        detailError: errorDe(action)
      }

    case CLAIM_ACTION_REQUEST:
      return {
        ...state,
        isSaving: true,
        saveError: null
      }

    case CLAIM_ACTION_SUCCESS: {
      const payload = action.data || action.response || null
      return {
        ...state,
        isSaving: false,
        saveError: null,
        acciones: state.acciones + 1,
        needRefresh: true,
        currentClaim: payload
          ? { ...state.currentClaim, ...payload }
          : state.currentClaim
      }
    }

    case CLAIM_ACTION_FAILURE:
      return {
        ...state,
        isSaving: false,
        saveError: errorDe(action)
      }

    default:
      return state
  }
}
